import { useState, useEffect, useRef } from 'react';

import { useParams, useNavigate, useLocation } from 'react-router-dom';

import { useQuery, useMutation } from '@tanstack/react-query';

import { getApiErrorMessage } from 'utils/apiFieldErrors';
import { usePolicyGroup } from 'hooks/usePolicyGroup';

import {
  Box,
  Button,
  Flex,
  Spinner,
  useDisclosure,
  useToast,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
} from '@chakra-ui/react';

import Card from 'components/card/Card';
import PolicyUploadModal from 'components/modals/PolicyUploadModal';
import LinkPoliciesModal from 'components/modals/LinkPoliciesModal';
import PolicyTabs, { getPolicyRoute } from './components/PolicyTabs';

import AutoFormView from 'views/auto_form/AutoFormView';
import HomeFormView from 'views/home_form/HomeFormView';
import ValidatedPolicy from 'views/validated_policy/ValidatedPolicyView';
import SpecialtyPolicyView from 'views/specialty_policy/SpecialtyPolicyView';
import LegacyAutoSubmissionView from 'views/legacy_auto_submission';
import LifeInsuranceFormView from 'views/life_insurance_form/LifeInsuranceFormView';
import CommercialPolicyView from 'views/commercial_policy/CommercialPolicyView';

import {
  apiGetSubmission,
  apiRemoveSubmissionsPolicy,
  apiCreateLegacyAutoPolicy,
} from 'api';

import {
  APISubmissionOverviewData,
  APISubmissionPolicyData,
} from 'types/api.types';

import {
  VALIDATION_TYPE_VALIDATE_AUTO_QUOTE,
  VALIDATION_TYPE_VALIDATE_HOME_QUOTE,
  VALIDATION_TYPE_MC_LIFE,
  VALIDATION_TYPE_COMMERCIAL_PROPERTY_CASUALTY,
  VALIDATION_TYPE_COMMERCIAL_AUTO,
} from 'variables/constants';

const SubmissionOverviewView = () => {
  const { id, uuid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  const [policyToDelete, setPolicyToDelete] = useState<{
    uuid: string;
    type: string;
    index: number;
    is_loa: boolean | null;
  } | null>(null);
  const [isCreatingLegacyAuto, setIsCreatingLegacyAuto] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const {
    isOpen: isModalOpen,
    onOpen: onModalOpen,
    onClose: onModalClose,
  } = useDisclosure();

  const {
    isOpen: isDeleteAlertOpen,
    onOpen: onDeleteAlertOpen,
    onClose: onDeleteAlertClose,
  } = useDisclosure();

  const {
    isOpen: isLinkModalOpen,
    onOpen: onLinkModalOpen,
    onClose: onLinkModalClose,
  } = useDisclosure();

  const {
    data: submissionData,
    isLoading: isLoadingSubmission,
    error: submissionError,
    refetch: refetchSubmission,
  } = useQuery<APISubmissionOverviewData>({
    queryKey: ['submission', id],
    queryFn: () => apiGetSubmission(parseInt(id!)),
  });

  const {
    existingGroupId,
    hasGroupedPolicies,
    sortedPolicies,
    isPolicyLinked,
    canLinkBinders,
    linkBinders,
    unlinkBinder,
    isLinking,
    isUnlinking,
  } = usePolicyGroup({
    policies: submissionData?.policies,
    refetchSubmission,
  });

  const deletePolicyMutation = useMutation({
    mutationFn: (policyUuid: string) => apiRemoveSubmissionsPolicy(policyUuid),
    onSuccess: async () => {
      toast({
        title: 'Policy removed successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      await refetchSubmission();
      navigate(`/admin/policies/submission/${id}`);
      onDeleteAlertClose();
      setPolicyToDelete(null);
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to remove policy',
        description: getApiErrorMessage(error, 'An error occurred'),
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
      onDeleteAlertClose();
      setPolicyToDelete(null);
    },
  });

  const handleModalClose = () => {
    onModalClose();
  };

  const handleUploadComplete = async (
    uploadedUuid?: string,
    policyType?: ValidationType,
  ) => {
    handleModalClose();
    if (uploadedUuid) {
      await refetchSubmission();

      const isLife = policyType === VALIDATION_TYPE_MC_LIFE;
      const isSpecialty =
        policyType === VALIDATION_TYPE_VALIDATE_AUTO_QUOTE ||
        policyType === VALIDATION_TYPE_VALIDATE_HOME_QUOTE;
      const isCommercial =
        policyType === VALIDATION_TYPE_COMMERCIAL_PROPERTY_CASUALTY ||
        policyType === VALIDATION_TYPE_COMMERCIAL_AUTO;

      const route = isLife
        ? `/admin/policies/submission/${id}/${uploadedUuid}/life`
        : isSpecialty
          ? `/admin/policies/submission/${id}/${uploadedUuid}/specialty`
          : isCommercial
            ? `/admin/policies/submission/${id}/${uploadedUuid}/commercial`
            : `/admin/policies/submission/${id}/${uploadedUuid}/validations`;

      navigate(route);
    }
  };

  const handleLegacyAutoSelect = async () => {
    if (!submissionData?.id) return;

    setIsCreatingLegacyAuto(true);

    try {
      const policy = await apiCreateLegacyAutoPolicy(submissionData.id);

      toast({
        title: 'Policy created successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });

      await refetchSubmission();
      navigate(`/admin/policies/submission/${id}/${policy.uuid}/legacy-auto`);
      handleModalClose();
    } catch (error: any) {
      toast({
        title: 'Failed to create policy',
        description: error?.error || error?.message || 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setIsCreatingLegacyAuto(false);
    }
  };

  const handleDeletePolicy = (
    policy: APISubmissionPolicyData,
    index: number,
    e: React.MouseEvent,
  ) => {
    e.stopPropagation();
    setPolicyToDelete({
      uuid: policy.uuid,
      type: policy.type,
      index: index + 1,
      is_loa: policy.is_loa,
    });
    onDeleteAlertOpen();
  };

  const handleConfirmDelete = () => {
    if (policyToDelete) {
      deletePolicyMutation.mutate(policyToDelete.uuid);
    }
  };

  const handleCancelDelete = () => {
    onDeleteAlertClose();
    setPolicyToDelete(null);
  };

  const handleTabSelect = (policy: APISubmissionPolicyData) => {
    navigate(getPolicyRoute(policy, id!));
  };

  const handleBinderOverviewClick = (policyUuid: string) => {
    navigate(`/admin/policies/overview/${policyUuid}`);
  };

  const selectedTabIndex = uuid
    ? sortedPolicies.findIndex((policy) => policy.uuid === uuid)
    : -1;

  const selectedPolicy = uuid
    ? sortedPolicies.find((policy) => policy.uuid === uuid) || null
    : null;

  const showValidations = uuid && location.pathname.endsWith('/validations');
  const showSpecialty = uuid && location.pathname.endsWith('/specialty');
  const showLegacyAuto = uuid && location.pathname.endsWith('/legacy-auto');
  const showLife = uuid && location.pathname.endsWith('/life');
  const showPersonal = uuid && location.pathname.endsWith('/personal');
  const showCommercial = uuid && location.pathname.endsWith('/commercial');

  const showAutoForm = showPersonal && selectedPolicy?.type === 'Auto';
  const showHomeForm = showPersonal && selectedPolicy?.type === 'Home';

  useEffect(() => {
    if (!submissionData?.policies?.length) return;

    const currentPath = location.pathname;
    const hasExplicitSuffix =
      currentPath.endsWith('/validations') ||
      currentPath.endsWith('/specialty') ||
      currentPath.endsWith('/life') ||
      currentPath.endsWith('/legacy-auto') ||
      currentPath.endsWith('/personal');

    if (hasExplicitSuffix) return;

    const policy = uuid
      ? submissionData.policies.find((p) => p.uuid === uuid)
      : submissionData.policies[0];

    if (!policy) return;

    const correctRoute = getPolicyRoute(policy, id!);
    if (currentPath !== correctRoute) {
      navigate(correctRoute, { replace: true });
    }
  }, [submissionData, uuid, id, navigate, location.pathname]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (isLoadingSubmission) {
    return (
      <Card>
        <Flex align="center" justify="center">
          <Spinner size="sm" mr="10px" /> Loading submission data...
        </Flex>
      </Card>
    );
  }

  if (submissionError || !submissionData) {
    return (
      <Card>
        <Flex align="center" justify="center" color="red.500">
          Error loading submission data. Please try again.
        </Flex>
      </Card>
    );
  }

  return (
    <>
      {(showAutoForm || showHomeForm) && <Box mt="50px" />}

      <PolicyTabs
        policies={sortedPolicies}
        selectedTabIndex={selectedTabIndex}
        isPolicyLinked={isPolicyLinked}
        onTabSelect={handleTabSelect}
        onUnlink={unlinkBinder}
        onDeleteClick={handleDeletePolicy}
        onAddClick={onModalOpen}
        onLinkClick={onLinkModalOpen}
        onBinderOverviewClick={handleBinderOverviewClick}
        isUnlinking={isUnlinking}
        isDeleting={deletePolicyMutation.isPending}
        hasGroupedPolicies={hasGroupedPolicies}
        canLinkBinders={canLinkBinders()}
      />

      {showValidations && (
        <ValidatedPolicy
          key={uuid}
          policyUuid={uuid}
          submissionId={submissionData.id}
          onValidationResults={refetchSubmission}
        />
      )}

      {showSpecialty && (
        <SpecialtyPolicyView
          key={uuid}
          policyUuid={uuid}
          submissionId={submissionData.id}
          onProcessingComplete={refetchSubmission}
          runner={selectedPolicy?.runner}
          isLOA={selectedPolicy?.is_loa || false}
        />
      )}

      {showLegacyAuto && (
        <LegacyAutoSubmissionView key={uuid} policyUuid={uuid} />
      )}

      {showLife && <LifeInsuranceFormView />}

      {showCommercial && (
        <CommercialPolicyView
          key={uuid}
          policyUuid={uuid}
          submissionId={submissionData.id}
          onProcessingComplete={refetchSubmission}
          policyType={
            selectedPolicy?.type === 'Commercial Property & Casualty'
              ? VALIDATION_TYPE_COMMERCIAL_PROPERTY_CASUALTY
              : selectedPolicy?.type === 'Commercial Auto'
                ? VALIDATION_TYPE_COMMERCIAL_AUTO
                : undefined
          }
        />
      )}

      {showAutoForm && <AutoFormView key={uuid} />}

      {showHomeForm && <HomeFormView key={uuid} />}

      <PolicyUploadModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onUploadComplete={handleUploadComplete}
        onLegacyAutoSelect={handleLegacyAutoSelect}
        submissionId={submissionData?.id}
        isCreatingSubmission={isCreatingLegacyAuto}
        province={submissionData?.province}
      />

      <LinkPoliciesModal
        isOpen={isLinkModalOpen}
        onClose={onLinkModalClose}
        policies={submissionData?.policies || []}
        onLinkBinders={linkBinders}
        isLinking={isLinking}
        existingGroupId={existingGroupId}
      />

      <AlertDialog
        isOpen={isDeleteAlertOpen}
        leastDestructiveRef={cancelRef}
        onClose={handleCancelDelete}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Remove Binder
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete{' '}
              <strong>
                {policyToDelete?.type} {policyToDelete?.index}
                {policyToDelete?.is_loa ? ' (LOA)' : ''}
              </strong>
              ? This action cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={handleCancelDelete}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleConfirmDelete}
                ml={3}
                isLoading={deletePolicyMutation.isPending}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default SubmissionOverviewView;
